import { useState, useMemo } from 'react';

import { notify } from '../../../app';
import { googleConnect } from '../../../api';
import { createWidgetHostUrl } from './createWidgetHostUrl';
import { useRequest, useAgentsOptions } from '../../../hooks';
import { usePresentationsManagement } from '../../knowledge-management/PresentationsManagement/hooks/usePresentationsManagement';
import { localTimeToUTC } from '../../../utilities';
import type { ConnectFormProps } from './types';
import type { OptionType } from '../../../components/Multiselect';

export const useConnect = () => {
  const [formId, setFormId] = useState(1);
  const [form, setForm] = useState<ConnectFormProps | null>(null);

  const { presentations, loading: loadingPresentations } =
    usePresentationsManagement({ initialPageSize: 200 });

  const presentationOptions = useMemo<OptionType[]>(() => {
    return (presentations || []).map(presentation => ({
      value: presentation.id || '',
      label: presentation.title || 'Untitled Presentation',
    }));
  }, [presentations]);

  const { isAgentsLoading, agentsOptions } = useAgentsOptions();

  const [handleCall, isCalling] = useRequest(
    _signal => {
      if (!form) return;
      const {
        agent_id,
        meet_link,
        bg_info,
        presentation_id,
        join_at,
        override_agent_name,
      } = form;

      const bot_name =
        override_agent_name ||
        agentsOptions.find(a => a.value === agent_id)?.label;
      const joinAt = localTimeToUTC(join_at);

      return googleConnect({
        meet_link: meet_link,
        bg_info: bg_info || undefined,
        bot_name: bot_name || undefined,
        widget_host_url: createWidgetHostUrl(form),
        presentation_id: presentation_id || undefined,
        join_at: joinAt || undefined,
      });
    },
    {
      onSuccess: () => {
        notify.success('A call has been initiated');
      },
      onError: () => {
        notify.error('Failed to initiate a call');
      },
      deps: [form],
    }
  );

  return {
    form,
    formId: String(formId),
    onChange: setForm,
    setFormId,
    setForm,
    handleCall,
    isCalling,
    agents: agentsOptions,
    presentations: presentationOptions,

    loading: isAgentsLoading || loadingPresentations,
  };
};
